/**
 * SaveData contract (contracts.md): serialization and version check.
 * Domain-only.
 */

import type { GameSession } from "./session.js";
import type { MapProgress } from "./map-progress.js";
import { type MageProfile, createMageProfile, mergeMageStats } from "./mage-profile.js";
import type { GemMods } from "./entities.js";
import type { GemType } from "./types.js";

export const SAVE_VERSION = 1;

export interface SavedMapProgress {
  unlockedFieldIds: string[];
  completedFieldIds: string[];
  stars: Record<string, number>;
  seenStoryBeats?: string[];
}


export interface SerializedGem {
  type: GemType;
  level: number;
  secondaryType?: GemType;
  mods?: GemMods;
}

export interface CurrentSessionSnapshot {
  fieldId: string;
  waveIndex: number;
  mana: number;
  maxMana: number;
  towersBuilt: number;
  rngSeed: number;
  towers: Array<{
    cell: { row: number; col: number };
    type: string;
    gems: Array<SerializedGem | null>;
  }>;
  gemInventory?: SerializedGem[];
}

export interface SaveData {
  version: number;
  mapProgress?: SavedMapProgress;
  skills?: Record<string, number>;
  wizardLevel?: number;
  currentSession?: CurrentSessionSnapshot;
  mageProfile?: MageProfile;
}

export function createEmptySave(): SaveData {
  return {
    version: SAVE_VERSION,
    mageProfile: createMageProfile(),
  };
}

export function serializeMapProgress(progress: MapProgress): SavedMapProgress {
  return {
    unlockedFieldIds: [...progress.unlockedFieldIds],
    completedFieldIds: [...progress.completedFieldIds],
    stars: Object.fromEntries(progress.stars),
    seenStoryBeats: [...progress.seenStoryBeats],
  };
}

export function deserializeMapProgress(saved: SavedMapProgress): MapProgress {
  return {
    unlockedFieldIds: new Set(saved.unlockedFieldIds ?? []),
    completedFieldIds: new Set(saved.completedFieldIds ?? []),
    stars: new Map(Object.entries(saved.stars ?? {})),
    seenStoryBeats: new Set(saved.seenStoryBeats ?? []),
  };
}

function serializeGem(gem: { type: GemType; level: number; secondaryType?: GemType; mods?: GemMods }): SerializedGem {
  const s: SerializedGem = { type: gem.type, level: gem.level };
  if (gem.secondaryType !== undefined) s.secondaryType = gem.secondaryType;
  if (gem.mods !== undefined) s.mods = { ...gem.mods };
  return s;
}

export function serializeSession(session: GameSession): CurrentSessionSnapshot {
  return {
    fieldId: session.field.id,
    waveIndex: session.currentWaveIndex,
    mana: session.mana.current,
    maxMana: session.mana.max,
    towersBuilt: session.towersBuilt,
    rngSeed: session.rngSeed ?? 0,
    towers: session.towers.map((t) => ({
      cell: { ...t.cell },
      type: t.type,
      gems: t.gemSlots.map((s) => s.gem ? serializeGem(s.gem) : null),
    })),
    gemInventory: session.gemInventory.length
      ? session.gemInventory.map(serializeGem)
      : undefined,
  };
}

/**
 * Apply a CurrentSessionSnapshot onto an already-created GameSession.
 * Caller is responsible for creating the session via createSession first.
 * Restores fields that serializeSession captured: rngSeed, mana, waveIndex, etc.
 * Gem secondaryType/mods are preserved so combined gems survive a save/load cycle.
 */
export function applySessionSnapshot(session: GameSession, snap: CurrentSessionSnapshot): void {
  session.rngSeed = snap.rngSeed;
}

export function deserializeSaveData(raw: unknown): SaveData | null {
  if (raw == null || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const version = o.version;
  if (typeof version !== "number" || version > SAVE_VERSION || version < 1) {
    return null;
  }
  // Migration: if mageProfile is absent (old save), default to empty profile.
  const mageProfile: MageProfile =
    o.mageProfile != null && typeof o.mageProfile === "object"
      ? {
          totalXp:
            typeof (o.mageProfile as Record<string, unknown>).totalXp === "number"
              ? ((o.mageProfile as Record<string, unknown>).totalXp as number)
              : 0,
          stageHighestXps:
            (o.mageProfile as Record<string, unknown>).stageHighestXps != null &&
            typeof (o.mageProfile as Record<string, unknown>).stageHighestXps === "object"
              ? ((o.mageProfile as Record<string, unknown>).stageHighestXps as Record<string, number>)
              : {},
          // Migration: old saves without skillLevels → default to empty {}
          skillLevels:
            (o.mageProfile as Record<string, unknown>).skillLevels != null &&
            typeof (o.mageProfile as Record<string, unknown>).skillLevels === "object"
              ? ((o.mageProfile as Record<string, unknown>).skillLevels as Record<number, number>)
              : {},
          // Migration: old saves without stats (or partial stats) → merge with defaults
          stats: mergeMageStats(
            (o.mageProfile as Record<string, unknown>).stats != null &&
              typeof (o.mageProfile as Record<string, unknown>).stats === "object"
              ? (o.mageProfile as Record<string, unknown>).stats
              : undefined
          ),
          // Migration: old saves without unlockedAchievements → default to []
          unlockedAchievements: Array.isArray(
            (o.mageProfile as Record<string, unknown>).unlockedAchievements
          )
            ? ((o.mageProfile as Record<string, unknown>).unlockedAchievements as string[])
            : [],
        }
      : createMageProfile();

  return {
    version,
    mapProgress: o.mapProgress as SaveData["mapProgress"],
    skills: o.skills as SaveData["skills"],
    wizardLevel: o.wizardLevel as SaveData["wizardLevel"],
    currentSession: o.currentSession as SaveData["currentSession"],
    mageProfile,
  };
}
